/* ==========================================================================
   CocktailBox — Global GSAP Animations
   Two systems:
     1. Section Reveal  — [data-reveal] on containers
     2. Global Parallax — [data-parallax="trigger"] on elements
   ========================================================================== */

if (!window.gsap || !window.ScrollTrigger) {
  // CDN failed: show everything instead of leaving it transparent
  document.documentElement.classList.add('cb-no-gsap');
} else {
  gsap.registerPlugin(ScrollTrigger);
}

/* --------------------------------------------------------------------------
   1. SECTION REVEAL
   --------------------------------------------------------------------------
   Usage (Liquid):
     <section class="cb-features" data-reveal>
       <div class="cb-features__header"> ... </div>
       <div class="cb-features__grid"> ... </div>
     </section>

   All direct children of [data-reveal] animate in with:
     - slight upward movement (y)
     - opacity 0 → 1
     - blur → clear
     - stagger between siblings

   Optional attributes on the [data-reveal] container:
     data-reveal-y="30"         → custom Y offset in px (default: 20)
     data-reveal-blur="8"       → custom blur in px (default: 4)
     data-reveal-stagger="0.15" → custom stagger in seconds (default: 0.12)
     data-reveal-duration="1"   → custom duration in seconds (default: 0.8)
     data-reveal-ease="power2"  → custom ease (default: "power2.out")
     data-reveal-disable="mobile" → disable on breakpoint
   -------------------------------------------------------------------------- */

function initSectionReveal() {
  var mm = gsap.matchMedia();

  mm.add(
    {
      isMobile: "(max-width: 479px)",
      isMobileLandscape: "(max-width: 767px)",
      isTablet: "(max-width: 991px)",
      isDesktop: "(min-width: 992px)",
      reduceMotion: "(prefers-reduced-motion: reduce)"
    },
    function (context) {
      var conditions = context.conditions;

      // Respect prefers-reduced-motion: just reveal without animation
      if (conditions.reduceMotion) {
        gsap.set("[data-reveal] > *", { opacity: 1 });
        return;
      }

      var ctx = gsap.context(function () {
        document.querySelectorAll("[data-reveal]").forEach(function (container) {
          // Check responsive disable
          var disable = container.getAttribute("data-reveal-disable");
          if (
            (disable === "mobile" && conditions.isMobile) ||
            (disable === "mobileLandscape" && conditions.isMobileLandscape) ||
            (disable === "tablet" && conditions.isTablet)
          ) {
            gsap.set(container.children, { opacity: 1 });
            return;
          }

          var children = container.children;
          if (!children.length) return;

          // Read optional overrides
          var yVal = parseFloat(container.getAttribute("data-reveal-y")) || 20;
          var blurVal = parseFloat(container.getAttribute("data-reveal-blur")) || 4;
          var staggerVal = parseFloat(container.getAttribute("data-reveal-stagger")) || 0.20;
          var durationVal = parseFloat(container.getAttribute("data-reveal-duration")) || 1;
          var easeVal = container.getAttribute("data-reveal-ease") || "power2.out";

          var tween = gsap.fromTo(children, {
            opacity: 0,
            y: yVal,
            filter: "blur(" + blurVal + "px)"
          }, {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            clearProps: "filter,transform",
            duration: durationVal,
            ease: easeVal,
            stagger: staggerVal,
            scrollTrigger: {
              trigger: container,
              start: "top 85%",
              toggleActions: "play none none none"
            }
          });

          // Keyboard and screen reader users: reveal at once when focus enters
          container.addEventListener("focusin", function () {
            if (tween.progress() < 1) tween.progress(1);
          });
        });
      });

      return function () { ctx.revert(); };
    }
  );
}

/* --------------------------------------------------------------------------
   2. GLOBAL PARALLAX (Osmo-inspired)
   --------------------------------------------------------------------------
   Usage (Liquid):
     <div data-parallax="trigger" data-parallax-start="20" data-parallax-end="-20">
       <img src="..." />
     </div>

   Attributes on the [data-parallax="trigger"] element:
     data-parallax="trigger"              → required, marks the element
     data-parallax="target"               → optional, on a child to animate instead
     data-parallax-direction="horizontal"  → "horizontal" or "vertical" (default)
     data-parallax-start="20"             → start position % (default: 20)
     data-parallax-end="-20"              → end position % (default: -20)
     data-parallax-scrub="2"              → scrub value in seconds (default: true)
     data-parallax-scroll-start="top bottom" → ScrollTrigger start (default)
     data-parallax-scroll-end="bottom top"   → ScrollTrigger end (default)
     data-parallax-disable="mobile"       → disable on breakpoint

   Tip for background images:
     Parent (trigger): overflow: hidden, position: relative
     Inner wrapper (target): height > 100% (e.g. 120%), position: absolute
     Image inside fills the wrapper
   -------------------------------------------------------------------------- */

function initGlobalParallax() {
  var mm = gsap.matchMedia();

  mm.add(
    {
      isMobile: "(max-width: 479px)",
      isMobileLandscape: "(max-width: 767px)",
      isTablet: "(max-width: 991px)",
      isDesktop: "(min-width: 992px)"
    },
    function (context) {
      var conditions = context.conditions;

      var ctx = gsap.context(function () {
        document.querySelectorAll('[data-parallax="trigger"]').forEach(function (trigger) {
          // Check responsive disable
          var disable = trigger.getAttribute("data-parallax-disable");
          if (
            (disable === "mobile" && conditions.isMobile) ||
            (disable === "mobileLandscape" && conditions.isMobileLandscape) ||
            (disable === "tablet" && conditions.isTablet)
          ) {
            return;
          }

          // Target: child with data-parallax="target", or the trigger itself
          var target = trigger.querySelector('[data-parallax="target"]') || trigger;

          // Direction → property
          var direction = trigger.getAttribute("data-parallax-direction") || "vertical";
          var prop = direction === "horizontal" ? "xPercent" : "yPercent";

          // Scrub
          var scrubAttr = trigger.getAttribute("data-parallax-scrub");
          var scrub = scrubAttr ? parseFloat(scrubAttr) : true;

          // Start / end position (%)
          var startAttr = trigger.getAttribute("data-parallax-start");
          var startVal = startAttr !== null ? parseFloat(startAttr) : 20;

          var endAttr = trigger.getAttribute("data-parallax-end");
          var endVal = endAttr !== null ? parseFloat(endAttr) : -20;

          // ScrollTrigger start / end
          var scrollStartRaw = trigger.getAttribute("data-parallax-scroll-start") || "top bottom";
          var scrollStart = "clamp(" + scrollStartRaw + ")";

          var scrollEndRaw = trigger.getAttribute("data-parallax-scroll-end") || "bottom top";
          var scrollEnd = "clamp(" + scrollEndRaw + ")";

          gsap.fromTo(
            target,
            { [prop]: startVal },
            {
              [prop]: endVal,
              ease: "none",
              scrollTrigger: {
                trigger: trigger,
                start: scrollStart,
                end: scrollEnd,
                scrub: scrub
              }
            }
          );
        });
      });

      return function () { ctx.revert(); };
    }
  );
}

/* --------------------------------------------------------------------------
   Init on DOM ready
   -------------------------------------------------------------------------- */
function bootAnimations() {
  if (!window.gsap || !window.ScrollTrigger) return;
  initSectionReveal();
  initGlobalParallax();
}
// Loaded by window.cbLoadLater (layout/theme.liquid), usually after DOMContentLoaded
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", bootAnimations);
else bootAnimations();
